import React, { createContext, useContext, useReducer } from 'react'
import { ModalLoginFail } from '../components/modal/ModalLoginFail'
import { ModalAfterMove } from '../components/modal/ModalAfterMove'
import { ModalWithDrawMember } from '../components/modal/ModalWithDrawMember'
import { ModalServiceAlert } from '../components/modal/ModalServiceAlert'
import { ModalPP } from '../components/modal/ModalPP'
import { ModalCaddieProfile } from '../components/modal/ModalCaddieProfile'
import { ModalProProfile } from '../components/modal/ModalProProfile'

// 모달 타입 정의
export const MODAL_TYPES = {
	LOGIN_FAIL: 'LOGIN_FAIL',
	AFTER_MOVE: 'AFTER_MOVE',
	WITHDRAW_MEMBER: 'WITHDRAW_MEMBER',
	SERVICE_ALERT: 'SERVICE_ALERT',
	PRIVACY_POLICY: 'PRIVACY_POLICY',
	TERMS: 'TERMS',
	PASSWORD_CHANGE: 'PASSWORD_CHANGE',
	PHONE_CHANGE: 'PHONE_CHANGE',
	CADDIE_PROFILE: 'CADDIE_PROFILE',
	PRO_PROFILE: 'PRO_PROFILE',
	// 필요에 따라 추가
}

// 초기 상태
const initialState = {
	modals: [], // 현재 열린 모달들의 배열
}

// 액션 타입
const MODAL_ACTIONS = {
	OPEN_MODAL: 'OPEN_MODAL',
	CLOSE_MODAL: 'CLOSE_MODAL',
	CLOSE_ALL_MODALS: 'CLOSE_ALL_MODALS',
}

// 리듀서
const modalReducer = (state, action) => {
	switch (action.type) {
		case MODAL_ACTIONS.OPEN_MODAL:
			return {
				...state,
				modals: [
					...state.modals,
					{
						id: action.id,
						type: action.modalType,
						props: action.props || {},
						onClose: action.onClose,
					},
				],
			}
		case MODAL_ACTIONS.CLOSE_MODAL:
			return {
				...state,
				modals: state.modals.filter((modal) => modal.id !== action.id),
			}
		case MODAL_ACTIONS.CLOSE_ALL_MODALS:
			return {
				...state,
				modals: [],
			}
		default:
			return state
	}
}

// Context 생성
const ModalContext = createContext()

// 모달 컴포넌트 매핑
const MODAL_COMPONENTS = {
	LOGIN_FAIL: ModalLoginFail,
	AFTER_MOVE: ModalAfterMove,
	WITHDRAW_MEMBER: ModalWithDrawMember,
	SERVICE_ALERT: ModalServiceAlert,
	PRIVACY_POLICY: ModalPP,
	CADDIE_PROFILE: ModalCaddieProfile,
	PRO_PROFILE: ModalProProfile,
}

// 모달 렌더링 컴포넌트
const ModalRenderer = ({ modals, closeModal }) => {
	if (!modals || !Array.isArray(modals)) {
		return null
	}

	return (
		<>
			{modals.map((modal) => {
				const ModalComponent = MODAL_COMPONENTS[modal.type]

				if (!ModalComponent) {
					console.warn(`Modal component not found for type: ${modal.type}`)
					return null
				}

				const modalProps = {
					...modal.props,
					modalVisible: true,
					onClose: () => {
						if (modal.onClose) {
							modal.onClose()
						}
						closeModal(modal.id)
					},
				}

				return <ModalComponent key={modal.id} {...modalProps} />
			})}
		</>
	)
}

// Provider 컴포넌트
export const ModalProvider = ({ children }) => {
	const [state, dispatch] = useReducer(modalReducer, initialState)

	const openModal = (modalType, props = {}, onClose = null) => {
		const id = `${modalType}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
		dispatch({
			type: MODAL_ACTIONS.OPEN_MODAL,
			id,
			modalType,
			props,
			onClose,
		})
		return id
	}

	const closeModal = (id) => {
		dispatch({
			type: MODAL_ACTIONS.CLOSE_MODAL,
			id,
		})
	}

	const closeAllModals = () => {
		dispatch({
			type: MODAL_ACTIONS.CLOSE_ALL_MODALS,
		})
	}

	const value = {
		modals: state.modals,
		openModal,
		closeModal,
		closeAllModals,
	}

	return (
		<ModalContext.Provider value={value}>
			{children}
			<ModalRenderer modals={state.modals} closeModal={closeModal} />
		</ModalContext.Provider>
	)
}

// useModalContext 훅 (내부용)
export const useModalContext = () => {
	const context = useContext(ModalContext)
	if (!context) {
		throw new Error('useModal must be used within a ModalProvider')
	}
	return context
}

// 개별 모달을 위한 훅
export const useModalActions = () => {
	const { openModal, closeModal, closeAllModals } = useModalContext()
	return { openModal, closeModal, closeAllModals }
}

/**
 * 모달을 쉽게 사용할 수 있는 커스텀 훅
 * @param {string} modalType - 모달 타입 (MODAL_TYPES에서 선택)
 * @param {object} defaultProps - 기본 props
 * @param {function} defaultOnClose - 기본 onClose 핸들러
 */
export const useModal = (modalType, defaultProps = {}, defaultOnClose = null) => {
	const { openModal, closeModal, closeAllModals } = useModalContext()

	const show = (props = {}, onClose = null) => {
		const mergedProps = { ...defaultProps, ...props }
		const mergedOnClose = onClose || defaultOnClose

		return openModal(modalType, mergedProps, mergedOnClose)
	}

	const hide = (modalId) => {
		closeModal(modalId)
	}

	const hideAll = () => {
		closeAllModals()
	}

	return {
		show,
		hide,
		hideAll,
		modalType,
	}
}

/**
 * 특정 모달 타입별로 미리 설정된 훅들
 */
export const useLoginFailModal = () => useModal(MODAL_TYPES.LOGIN_FAIL)
export const useAfterMoveModal = () => useModal(MODAL_TYPES.AFTER_MOVE)
export const useWithdrawMemberModal = () => useModal(MODAL_TYPES.WITHDRAW_MEMBER)
export const useServiceAlertModal = () => useModal(MODAL_TYPES.SERVICE_ALERT)
export const usePrivacyPolicyModal = () => useModal(MODAL_TYPES.PRIVACY_POLICY)
export const useTermsModal = () => useModal(MODAL_TYPES.TERMS)
export const usePasswordChangeModal = () => useModal(MODAL_TYPES.PASSWORD_CHANGE)
export const usePhoneChangeModal = () => useModal(MODAL_TYPES.PHONE_CHANGE)
export const useCaddieProfileModal = () => useModal(MODAL_TYPES.CADDIE_PROFILE)
export const useProProfileModal = () => useModal(MODAL_TYPES.PRO_PROFILE)
